import openai from './openaiClient';
import { 
  APIConnectionError,
  AuthenticationError,
  PermissionDeniedError,
  RateLimitError,
  InternalServerError
} from 'openai';
import { supabase } from '../lib/supabase';

/**
 * Maps OpenAI API error types to user-friendly error messages.
 * @param {Error} error - The error object from OpenAI API.
 * @returns {Object} Error info with isInternal flag and message.
 */
function getErrorMessage(error) {
  if (error instanceof AuthenticationError) {
    return { isInternal: true, message: 'خطأ في المصادقة. يرجى التحقق من مفتاح API.' };
  } else if (error instanceof PermissionDeniedError) {
    return { isInternal: true, message: 'تم تجاوز حد الاستخدام. يرجى المحاولة لاحقاً.' };
  } else if (error instanceof RateLimitError) {
    return { isInternal: true, message: 'تم تجاوز حد الطلبات. يرجى الانتظار قليلاً.' };
  } else if (error instanceof InternalServerError) {
    return { isInternal: true, message: 'خدمة الذكاء الاصطناعي غير متاحة حالياً. يرجى المحاولة لاحقاً.' };
  } else if (error instanceof APIConnectionError) {
    return { isInternal: true, message: 'فشل الاتصال بخدمة الذكاء الاصطناعي. يرجى التحقق من الاتصال بالإنترنت.' };
  } else {
    return { isInternal: false, message: error?.message || 'حدث خطأ غير متوقع. يرجى المحاولة مرة أخرى.' };
  }
}

/**
 * Generates comprehensive AI-powered recommendations for a client
 * @param {Object} client - Client object with portfolio and preferences
 * @param {Array} properties - Available properties for matching
 * @param {Array} clientContracts - Client's contract history
 * @returns {Promise<Object>} Recommendation object with property matches, timing insights, and market analysis
 */
export async function generateClientRecommendations(client, properties, clientContracts = []) {
  try {
    const clientProfile = `
اسم العميل: ${client?.name}
الفئة: ${client?.category}
الموقع المفضل: ${client?.preferredLocation || 'أي موقع'}
نطاق الميزانية: ${client?.budgetMin || 0} - ${client?.budgetMax || 'غير محدد'} دينار أردني
نوع العقار: ${client?.propertyType || 'أي نوع'}
عدد غرف النوم: ${client?.bedrooms || 'أي عدد'}
الأولوية: ${client?.priority}
الحالة: ${client?.status}
ملاحظات: ${client?.notes || 'لا توجد'}
    `?.trim();

    const propertiesData = properties?.slice(0, 20)?.map(p => ({
      id: p?.id,
      title: p?.title,
      type: p?.type,
      purpose: p?.purpose,
      bedrooms: p?.bedrooms,
      bathrooms: p?.bathrooms,
      area: p?.area,
      price: p?.price,
      city: p?.city,
      district: p?.district,
      status: p?.status,
      features: p?.features
    }));

    const contractHistory = clientContracts?.map(c => ({
      type: c?.contractType,
      date: c?.submittedDate,
      status: c?.status,
      propertyType: c?.propertyType
    }));

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o',
      messages: [
        { 
          role: 'system', 
          content: 'أنت مستشار عقاري خبير متخصص في تحليل محفظة العملاء وسلوكهم لتقديم توصيات ذكية. قم بتحليل بيانات العميل والعقارات المتاحة وتاريخ العقود لتقديم: 1) أفضل العقارات المطابقة مع درجات التطابق والأسباب، 2) التوقيت الأمثل لمراجعة العقود بناءً على السوق والموسمية، 3) رؤى السوق والاتجاهات ذات الصلة بتفضيلات العميل. قدم التوصيات باللغة العربية بشكل احترافي ومفصل.' 
        },
        { 
          role: 'user', 
          content: `قم بتحليل ملف العميل التالي وتقديم توصيات شاملة:\n\nملف العميل:\n${clientProfile}\n\nالعقارات المتاحة:\n${JSON.stringify(propertiesData, null, 2)}\n\nتاريخ العقود:\n${JSON.stringify(contractHistory, null, 2)}\n\nقدم توصيات مفصلة تشمل مطابقة العقارات، توقيت مراجعة العقود، ورؤى السوق.` 
        },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'client_recommendations',
          schema: {
            type: 'object',
            properties: {
              propertyMatches: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    propertyId: { type: 'string' },
                    matchScore: { type: 'number' },
                    reasoning: { type: 'string' },
                    highlights: { type: 'array', items: { type: 'string' } },
                    concerns: { type: 'array', items: { type: 'string' } }
                  },
                  required: ['propertyId', 'matchScore', 'reasoning']
                }
              },
              contractTiming: {
                type: 'object',
                properties: {
                  optimalReviewDate: { type: 'string' },
                  reasoning: { type: 'string' },
                  seasonalFactors: { type: 'array', items: { type: 'string' } },
                  urgencyLevel: { type: 'string' }
                },
                required: ['optimalReviewDate', 'reasoning', 'urgencyLevel']
              },
              marketInsights: {
                type: 'object',
                properties: {
                  currentTrends: { type: 'array', items: { type: 'string' } },
                  priceAnalysis: { type: 'string' },
                  investmentOpportunities: { type: 'array', items: { type: 'string' } },
                  riskFactors: { type: 'array', items: { type: 'string' } }
                },
                required: ['currentTrends', 'priceAnalysis']
              },
              summary: { type: 'string' }
            },
            required: ['propertyMatches', 'contractTiming', 'marketInsights', 'summary']
          }
        }
      },
      temperature: 0.7,
      max_tokens: 2500
    });

    const recommendations = JSON.parse(response?.choices?.[0]?.message?.content || '{}');

    // Enrich property matches with full property data
    const enrichedMatches = recommendations?.propertyMatches?.map(match => {
      const property = properties?.find(p => p?.id === match?.propertyId);
      return {
        ...match,
        property: property || null
      };
    });

    return {
      ...recommendations,
      propertyMatches: enrichedMatches,
      generatedAt: new Date()?.toISOString(),
      clientId: client?.id,
      clientName: client?.name
    };
  } catch (error) {
    console.error('AI Recommendation Generation Error:', error);
    const errorInfo = getErrorMessage(error);
    throw new Error(errorInfo?.message);
  }
}

/**
 * Saves a recommendation to the database with pending approval status
 * @param {Object} recommendation - Recommendation object from AI
 * @param {string} userId - User ID who generated the recommendation
 * @returns {Promise<Object>} Saved recommendation with ID
 */
export async function saveRecommendation(recommendation, userId) {
  try {
    const { data, error } = await supabase
      ?.from('client_recommendations')
      ?.insert({
        user_id: userId,
        client_id: recommendation?.clientId,
        recommendation_data: recommendation,
        approval_status: 'pending',
        priority: recommendation?.contractTiming?.urgencyLevel === 'عاجل' ? 'high' : 'medium'
      })
      ?.select()
      ?.single();

    if (error) {
      console.error('Error saving recommendation:', error);
      throw new Error(`فشل في حفظ التوصية: ${error?.message}`);
    }

    return {
      id: data?.id,
      clientId: data?.client_id,
      recommendationData: data?.recommendation_data,
      approvalStatus: data?.approval_status,
      priority: data?.priority,
      createdAt: data?.created_at
    };
  } catch (error) {
    console.error('Save recommendation error:', error);
    throw error;
  }
}

/**
 * Retrieves all recommendations with optional filtering
 * @param {string} userId - User ID
 * @param {Object} filters - Filter options (status, priority)
 * @returns {Promise<Array>} Array of recommendations
 */
export async function getRecommendations(userId, filters = {}) {
  try {
    let query = supabase
      ?.from('client_recommendations')
      ?.select('*, clients(name, email, phone, category)')
      ?.eq('user_id', userId)
      ?.order('created_at', { ascending: false });

    if (filters?.status && filters?.status !== 'all') {
      query = query?.eq('approval_status', filters?.status);
    }

    if (filters?.priority && filters?.priority !== 'all') {
      query = query?.eq('priority', filters?.priority);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching recommendations:', error);
      throw new Error(`فشل في تحميل التوصيات: ${error?.message}`);
    }

    return data?.map(row => ({
      id: row?.id,
      clientId: row?.client_id,
      clientName: row?.clients?.name,
      clientEmail: row?.clients?.email,
      clientPhone: row?.clients?.phone,
      clientCategory: row?.clients?.category,
      recommendationData: row?.recommendation_data,
      approvalStatus: row?.approval_status,
      priority: row?.priority,
      approvedBy: row?.approved_by,
      approvedAt: row?.approved_at,
      rejectionReason: row?.rejection_reason,
      sentToClient: row?.sent_to_client,
      sentAt: row?.sent_at,
      createdAt: row?.created_at
    })) || [];
  } catch (error) {
    console.error('Get recommendations error:', error);
    throw error;
  }
}

/**
 * Approves a recommendation and optionally sends it to the client
 * @param {string} recommendationId - Recommendation ID
 * @param {string} approverId - User ID of approver
 * @param {boolean} sendToClient - Whether to send to client immediately
 * @returns {Promise<Object>} Updated recommendation
 */
export async function approveRecommendation(recommendationId, approverId, sendToClient = false) {
  try {
    const { data, error } = await supabase
      ?.from('client_recommendations')
      ?.update({
        approval_status: 'approved',
        approved_by: approverId,
        approved_at: new Date()?.toISOString(),
        sent_to_client: sendToClient,
        sent_at: sendToClient ? new Date()?.toISOString() : null
      })
      ?.eq('id', recommendationId)
      ?.select()
      ?.single();

    if (error) {
      console.error('Error approving recommendation:', error);
      throw new Error(`فشل في الموافقة على التوصية: ${error?.message}`);
    }

    return {
      id: data?.id,
      approvalStatus: data?.approval_status,
      approvedBy: data?.approved_by,
      approvedAt: data?.approved_at,
      sentToClient: data?.sent_to_client
    };
  } catch (error) {
    console.error('Approve recommendation error:', error);
    throw error;
  }
}

/**
 * Rejects a recommendation with a reason
 * @param {string} recommendationId - Recommendation ID
 * @param {string} approverId - User ID of approver
 * @param {string} reason - Rejection reason
 * @returns {Promise<Object>} Updated recommendation
 */
export async function rejectRecommendation(recommendationId, approverId, reason) {
  try {
    const { data, error } = await supabase
      ?.from('client_recommendations')
      ?.update({
        approval_status: 'rejected',
        approved_by: approverId,
        approved_at: new Date()?.toISOString(),
        rejection_reason: reason
      })
      ?.eq('id', recommendationId)
      ?.select()
      ?.single();

    if (error) {
      console.error('Error rejecting recommendation:', error);
      throw new Error(`فشل في رفض التوصية: ${error?.message}`);
    }

    return {
      id: data?.id,
      approvalStatus: data?.approval_status,
      rejectionReason: data?.rejection_reason
    };
  } catch (error) {
    console.error('Reject recommendation error:', error);
    throw error;
  }
}

/**
 * Sends an approved recommendation to the client
 * @param {string} recommendationId - Recommendation ID
 * @returns {Promise<Object>} Updated recommendation
 */
export async function sendRecommendationToClient(recommendationId) {
  try {
    const { data, error } = await supabase
      ?.from('client_recommendations')
      ?.update({
        sent_to_client: true,
        sent_at: new Date()?.toISOString()
      })
      ?.eq('id', recommendationId)
      ?.eq('approval_status', 'approved')
      ?.select()
      ?.single();

    if (error) {
      console.error('Error sending recommendation:', error);
      throw new Error(`فشل في إرسال التوصية: ${error?.message}`);
    }

    return {
      id: data?.id,
      sentToClient: data?.sent_to_client,
      sentAt: data?.sent_at
    };
  } catch (error) {
    console.error('Send recommendation error:', error);
    throw error;
  }
}